import { Account } from '@radixdlt/radix-dapp-toolkit';
import { ActionFunctionArgs, json } from '@remix-run/cloudflare';
import { Link, useFetcher } from '@remix-run/react';
import AccountSelector from '~/components/account-selector';
import { getPairs } from '~/lib/models/pair';
import { getLPToken } from '~/lib/models/lpToken';
import { fetchTokensFromWallet, parsePoolState, useGatewayApi } from '~/rdt/gateway';
import { MyLiquidity } from '~/components/liquidity-my';
import { fetchPrices } from '~/lib/astrolescent';
import { setDbFromContext } from '~/lib/db';
import { fetchRewardPools } from '~/lib/radix-rewards';

export default function LiquidityIndex() {
   const fetcher = useFetcher<typeof action>();

   function onAccount(account: Account) {
      fetcher.submit({ account: account.address }, { method: 'POST' });
   }
   
   return (
     <div className="spacy-y-4 mx-4 mb-8 xl:mx-auto xl:w-3/5">
       <div className="flex items-center justify-between">
         <h1 className="py-4 text-left text-3xl font-bold lg:p-8 lg:text-4xl">My Liquidity</h1>
         <Link to="/liquidity/add" className="btn-white-small">
           Add Liquidity
         </Link>
       </div>

       <div className="flex flex-col space-y-4">
         <AccountSelector mode="dark" onAccount={onAccount}></AccountSelector>

         <MyLiquidity pairs={fetcher.data?.pairs || []} />
       </div>
     </div>
   );
}

export async function action({ context, request }: ActionFunctionArgs) {
  setDbFromContext(context);

  const { state } = useGatewayApi;

  const formData = await request.formData();
  const account = formData.get("account") as string;

  if (!account) {
    return json({
      error: "No address provided",
      pairs: [],
    });
  }

  const tokens = await fetchTokensFromWallet(account);
  const pairs = await getPairs();
  const prices = await fetchPrices();
  const rewardPairs = await fetchRewardPools();

  const tokenAddresses = tokens.map((t) => t.address);
  const myPairs = [];

  for (let pair of pairs) {
    if (tokenAddresses.includes(pair.Pair.baseLPToken)) {
      const token = tokens.find((t) => t.address == pair.Pair.baseLPToken);

      // remove DefiPlaza from name to make it shorter
      if (token?.name) {
        token.name = token.name.replace("Defiplaza ", "").replace("DefiPlaza ", "");
      }

      const [componentDetails] = await state.getEntityDetailsVaultAggregated([
        pair.Pair.basePool,
      ]);
      const poolState = parsePoolState(pair.Pair, componentDetails, true);
      const share = +token?.amount / +token?.totalSupply;

      const basePrice = prices[pair.Token.address];
      const quotePrice = prices[pair.Pair.quoteToken];
      const usdValue =
        share *
        (poolState.vaults[0].amount * basePrice?.tokenPriceUSD +
          poolState.vaults[1].amount * quotePrice?.tokenPriceUSD);

      myPairs.push({
        pair: pair.Pair,
        baseToken: pair.Token,
        quoteToken: pair.quoteToken,
        lpToken: token,
        poolState,
        isBase: true,
        amount: token.amount,
        usdValue,
        share,
        radixRewards: rewardPairs.includes(pair.Pair.address),
      });
    }

    if (tokenAddresses.includes(pair.Pair.quoteLPToken)) {
      const token = tokens.find((t) => t.address == pair.Pair.quoteLPToken);

      // remove DefiPlaza from name to make it shorter
      if (token?.name) {
        token.name = token.name.replace("Defiplaza ", "").replace("DefiPlaza ", "");
      }

      const [componentDetails] = await state.getEntityDetailsVaultAggregated([
        pair.Pair.quotePool,
      ]);
      const poolState = parsePoolState(pair.Pair, componentDetails, false);

      const share = +token?.amount / +token?.totalSupply;
      const basePrice = prices[pair.Token.address];
      const quotePrice = prices[pair.Pair.quoteToken];

      const usdValue =
        share *
        (poolState.vaults[1].amount * basePrice?.tokenPriceUSD +
          poolState.vaults[0].amount * quotePrice?.tokenPriceUSD);

      myPairs.push({
        pair: pair.Pair,
        baseToken: pair.Token,
        quoteToken: pair.quoteToken,
        lpToken: token,
        poolState,
        isBase: false,
        amount: token.amount,
        usdValue,
        share,
        radixRewards: rewardPairs.includes(pair.Pair.address),
      });
    }
  }

  myPairs.sort((a, b) => {
    return b.usdValue - a.usdValue;
  });

  return json({ pairs: myPairs });
}
